'use client';

import React, { FC, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { AnimatedShipIndicator } from '@tuic/web-compositions/shared/AnimatedShipIndicator';
import { DialogPersistent } from '@tuic/web-compositions/shared/DialogPersistent';
import { TextButton } from '@tuic/web-compositions/core/TextButton';
import { useToasts } from '@tuic/web-compositions/shared/Toaster';
import { Mxp365Iframe } from './Mxp365Iframe';
import type { MxpDialogTitleCollection, MxpIframeErrorHandler, MxpIframeRef, MxpIframeShareHandler, MxpIframeTitleChangeHandler } from './mxp365.types';
import { extractPathFromLocationUri, findTitleByPathPrefix } from '@modules/mxp-dialog/helpers';
import { useClientTranslation } from '@lib/i18n/client';
import { Mxp365DebugFooter } from './Mxp365DebugFooter';
import { Mxp365Error } from './Mxp365Error';
import { ErrorCode } from '@lib/errors/error-code';
import { Trans } from '@tuic/i18n';
import { captureError } from '@lib/sentry/capture-error-code';
import { createMxp365OpenEvent } from './events';
import { useDialogTelemetry } from '@modules/prism-client/lib/telemetry';
import type PrismAPI from '@modules/prism-client/types/prism-types';
type Props = {
  titles?: MxpDialogTitleCollection;
};
export type Mxp365DialogRef = {
  initiateNavigation: (path: string, title: string, token: string, queryParams?: Record<string, string>, telemetry?: PrismAPI.TelemetryInstruction[]) => void;
};
export const Mxp365Dialog = forwardRef<Mxp365DialogRef, Props>(({
  titles
}, ref) => {
  const MXP_DIALOG_NAV_ATTR = 'data-mxp-dialog-nav';
  const {
    t
  } = useClientTranslation();
  const {
    push
  } = useToasts();
  const iframeRef = useRef<MxpIframeRef>(null);
  const notOnProd = process.env.NODE_ENV !== 'production';

  // State management - kept separate for clarity
  const [isOpen, setIsOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState<string | null>(null);
  const [currentTitle, setCurrentTitle] = useState('MXP365');
  const [currentToken, setCurrentToken] = useState<string>('');
  const [currentQueryParams, setCurrentQueryParams] = useState<Record<string, string> | undefined>();
  const [telemetry, setTelemetry] = useState<PrismAPI.TelemetryInstruction[]>();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<ErrorCode | null>(null);
  const [showBackButton, setShowBackButton] = useState(false);
  const [dynamicTitle, setDynamicTitle] = useState<string | undefined>();
  useDialogTelemetry(telemetry, isOpen);

  // Simple iframe key for reload logic
  const [iframeKey, setIframeKey] = useState(0);

  // Track if dialog has been opened (for iframe mounting)
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  // Timeout for pages that don't send moduleReady
  const moduleReadyTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /**
   * Core navigation function - handles same path reload vs different path navigation
   */
  const initiateNavigation = useCallback((newPath: string, newTitle: string, newToken: string, newQueryParams?: Record<string, string>, newTelemetry?: PrismAPI.TelemetryInstruction[]) => {
    // Check if it's the same path (requires reload)
    if (currentPath === newPath) {
      // Same path - increment key to force iframe reload
      setIframeKey(prev => prev + 1);
    }

    // Update state
    setIsOpen(true);
    setCurrentPath(newPath);
    setDynamicTitle(undefined);
    setCurrentTitle(newTitle);
    setCurrentToken(newToken);
    setCurrentQueryParams(newQueryParams);
    setTelemetry(newTelemetry);
    setIsPending(true);
    setError(null);
    setHasBeenOpened(true);
  }, [currentPath]);

  // Expose navigation function to parent
  useImperativeHandle(ref, () => ({
    initiateNavigation
  }), [initiateNavigation]);

  // Simplified navigation handler from iframe
  const handleNavigation = useCallback(({
    allowedNavigateBack
  }: {
    allowedNavigateBack: boolean;
  }) => {
    // Clear any existing timeout
    clearTimeout(moduleReadyTimeoutRef.current);

    // Start a timeout to clear pending state if moduleReady doesn't arrive
    // This handles pages that don't send moduleReady events
    moduleReadyTimeoutRef.current = setTimeout(() => {
      setIsPending(false);
    }, 300);
    setShowBackButton(allowedNavigateBack);
  }, []);
  const handleBack = useCallback(() => {
    iframeRef.current?.postMessage({
      action: 'navigateBack'
    });
  }, []);
  const handleShare = useCallback<MxpIframeShareHandler>(async args => {
    if (!window.navigator.share) {
      return;
    }
    try {
      await window.navigator.share(args);
    } catch (e) {}
  }, []);
  const handleFavoriteAdded = useCallback(() => {
    let removeSuccessMessage = () => {};
    const handleClick = () => {
      removeSuccessMessage();
      // Use the open event to ensure dialog opens and navigates
      const event = createMxp365OpenEvent({
        path: '/favorites',
        title: t('Merkliste'),
        token: currentToken,
        queryParams: currentQueryParams
      });
      window.dispatchEvent(event);
    };
    removeSuccessMessage = push({
      title: t('Erfolgreich zur Merkliste hinzugefügt.'),
      text: <Trans>
          Alle gemerkten Inhalte und Aktivitäten finden Sie auf{' '}
          <TextButton {...{
          [MXP_DIALOG_NAV_ATTR]: true
        }} onClick={handleClick}>
            Ihrer Merkliste
          </TextButton>
        </Trans>
    });
  }, [push, t, currentToken, currentQueryParams]);

  // Simplified module ready handler
  const handleModuleReady = useCallback(() => {
    // Clear the timeout since moduleReady has arrived
    clearTimeout(moduleReadyTimeoutRef.current);
    setIsPending(false);
  }, []);
  const handleTitleChange = useCallback<MxpIframeTitleChangeHandler>(({
    locationUri
  }) => {
    if (!titles) return;
    const extractedPath = extractPathFromLocationUri(locationUri);
    const title = findTitleByPathPrefix(extractedPath, titles);
    if (title) {
      setDynamicTitle(title);
    }
  }, [titles]);
  const handleError: MxpIframeErrorHandler = ({
    type,
    payload
  }) => {
    let errorCode: ErrorCode;
    if (type === 'notFound') {
      errorCode = ErrorCode.Mxp365NotFound;
    } else if (payload?.title?.toLowerCase().includes('person not found')) {
      errorCode = ErrorCode.Mxp365PersonFromTokenNotFound;
    } else {
      errorCode = ErrorCode.Mxp365GeneralError;
      captureError(new Error(`General MXP error of type: "${type}"`));
    }
    setError(errorCode);
  };

  // Check for missing token
  useEffect(() => {
    if (isOpen && currentPath && !currentToken) {
      setError(ErrorCode.Mxp365MissingToken);
    }
  }, [isOpen, currentPath, currentToken]);

  // Navigate iframe when URL changes
  useEffect(() => {
    if (currentPath && iframeRef.current) {
      iframeRef.current.navigateTo(currentPath);
      setIsPending(true);
    }
  }, [currentPath]);
  const handleClose = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // Clear any pending timeout when dialog closes
      clearTimeout(moduleReadyTimeoutRef.current);
      // Reset pending state when closing
      setIsPending(true);
    }
  }, []);

  // Determine if iframe should be rendered
  const shouldRenderIframe = hasBeenOpened && currentPath;
  return <DialogPersistent open={isOpen} onOpenChange={handleClose} desktopSize="xl" disableSpacing onBack={showBackButton ? handleBack : undefined} showCloseButton expanded title={dynamicTitle ?? currentTitle} footerContent={notOnProd && <Mxp365DebugFooter getIframeUrl={iframeRef.current?.getIframeUrl} />}>
      <div className="relative bg-background-primary h-full text-marine-80 text-marine-high-emphasis">
        {shouldRenderIframe && currentPath && <Mxp365Iframe key={iframeKey} path={currentPath} token={currentToken} queryParams={currentQueryParams} className="bg-background-primary w-full h-full border-none relative" onMxpNavigate={handleNavigation} onMxpTitleChange={handleTitleChange} onMxpShare={handleShare} onMxpFavoriteAdded={handleFavoriteAdded} onMxpError={handleError} onMxpModuleReady={handleModuleReady} ref={iframeRef} />}

        {(isPending || error) && <div className="absolute inset-0 bg-[#f0f1f5] text-center text-marine-high-emphasis text-marine-80">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              {isPending && !error && <AnimatedShipIndicator on="white" />}
              {error && <Mxp365Error error={error} />}
            </div>
          </div>}
      </div>
    </DialogPersistent>;
});
Mxp365Dialog.displayName = 'Mxp365Dialog';